'use strict'

// personaSession.js — the ONE owner of client-side persona session storage
// (PERSONA-2, tickets/opus.md: "session isolation — a persona switch must not
// bleed into the admin's real session").
//
// ── Where persona state lives, and why ──────────────────────────────────
// `sessionStorage['symbols_persona_session']` — PER-TAB by construction.
// That single choice IS the cross-tab isolation semantics: a persona started
// in one tab never leaks into the admin's other tabs (each tab opts in), and
// it survives a reload of ITS OWN tab (sessionStorage persists across
// reloads), which is the client half of PERSONA-3's reload-survival
// requirement. Compare: the admin's REAL token lives in
// `localStorage['symbols_access_token']` (cross-tab, long-lived) — this
// module NEVER reads or writes that key, or any other localStorage key. The
// admin's base session is untouchable from here by construction.
//
// ── What the stored blob is ─────────────────────────────────────────────
// { token, sid, role, startedAt, expiresAt, workspaceId, actorWorkspaceRole }
//   token    — the persona-claimed JWT minted by POST /core/persona/start
//              (same identity as the admin's own token + a
//              `persona:{role,sid,startedAt}` claim; server 886a9b27 resolves
//              it server-side). Lives ONLY here — never copied into DOMQL
//              state, where state dumps/serializers could leak it.
//   role     — canonical workspace-tier role (admin|editor|viewer|guest).
//   sid      — the server session id (PERSONA-3's server nonce); revocation
//              happens server-side against it, this blob is just the pointer.
//   workspaceId — the workspace the session is bound to server-side; used by
//              the provider guard below to kill the persona on a workspace
//              switch instead of sending a cross-tenant sid.
//
// ── Failure directions (deliberate, tested) ─────────────────────────────
// * A MALFORMED blob is cleared and read as null — a corrupt persona must
//   degrade to the admin's honest base session, not to a half-applied lens.
// * An EXPIRED-but-present blob still returns its token to the provider —
//   the server 401s it honestly (and the worker's resolver refuses the sid).
//   Falling back to the base token here would silently restore full admin
//   power while the UI still shows the persona — the "confident wrong
//   answer" class PERSONA-1 exists to prevent. Only an explicit clear (exit,
//   workspace switch, boot cleanup) restores the base token.
// * A WORKSPACE-MISMATCHED blob (active-workspace pointer moved under it —
//   switch in this tab or convergence from another) is cleared by the
//   provider guard: the server would refuse the sid anyway ("scoped to a
//   different workspace"); clearing keeps the new workspace usable instead
//   of 401-storming it. Null/unknown pointer does NOT kill the persona —
//   the server cross-check stays the authority (fail toward honesty, not
//   toward silently dropping the lens).
//
// FA204 / frank trap (project_frank_drops_module_local_helpers): every
// exported function below carries self-contained inline copies of the
// storage key + validators — a bare module-local read is unresolvable once
// an export is individually frank.toJSON'd. Cross-file FUNCTION imports
// (getActiveWorkspacePointer) are the sanctioned pattern (same as
// sdkClient.js's imports).

import { getActiveWorkspacePointer } from './activeWorkspacePointer.js'

// Storage key + shape doc for consumers (PersonaPill / PERSONA-3's oracle
// flow / PERSONA-4's SDK docs). The exported const is for REFERENCE — the
// functions below inline the literal per the FA204 discipline.
export const PERSONA_SESSION_STORAGE_KEY = 'symbols_persona_session'

// READ the stored session or null. Malformed/unparseable → cleared + null.
export function readPersonaSession() {
  const KEY = 'symbols_persona_session'
  const storage = () => {
    try {
      return typeof globalThis.sessionStorage !== 'undefined' ? globalThis.sessionStorage : null
    } catch {
      return null
    }
  }
  const validShape = (s) =>
    !!s &&
    typeof s === 'object' &&
    typeof s.token === 'string' &&
    s.token.length > 0 &&
    typeof s.sid === 'string' &&
    s.sid.length > 0 &&
    ['admin', 'editor', 'viewer', 'guest'].includes(s.role) &&
    Number.isFinite(s.startedAt) &&
    Number.isFinite(s.expiresAt) &&
    typeof s.workspaceId === 'string' &&
    s.workspaceId.length > 0
  const store = storage()
  if (!store) return null
  let raw = null
  try {
    raw = store.getItem(KEY)
  } catch {
    return null
  }
  if (!raw) return null
  let parsed = null
  try {
    parsed = JSON.parse(raw)
  } catch {
    parsed = null
  }
  if (!validShape(parsed)) {
    // Corrupt blob — degrade to the honest base session, loudly.
    try {
      store.removeItem(KEY)
    } catch {
      /* locked storage — nothing else to do */
    }
    console.warn('[persona] malformed stored session cleared — back to the base session')
    return null
  }
  return parsed
}

// WRITE a session from the /core/persona/start response envelope. Strict:
// refuses (false + warn) anything that doesn't carry the full bounded shape —
// a partial write would half-apply the lens. Stores ONLY the known keys.
export function writePersonaSession(envelope) {
  const KEY = 'symbols_persona_session'
  const storage = () => {
    try {
      return typeof globalThis.sessionStorage !== 'undefined' ? globalThis.sessionStorage : null
    } catch {
      return null
    }
  }
  const src = envelope && typeof envelope === 'object' ? envelope : null
  const session = src
    ? {
        token: typeof src.token === 'string' ? src.token : null,
        sid: typeof src.sid === 'string' ? src.sid : null,
        role: src.role,
        startedAt: Number(src.startedAt),
        expiresAt: Number(src.expiresAt),
        workspaceId: typeof src.workspaceId === 'string' ? src.workspaceId : null,
        actorWorkspaceRole:
          typeof src.actorWorkspaceRole === 'string' ? src.actorWorkspaceRole : null
      }
    : null
  const valid =
    !!session &&
    !!session.token &&
    !!session.sid &&
    ['admin', 'editor', 'viewer', 'guest'].includes(session.role) &&
    Number.isFinite(session.startedAt) &&
    Number.isFinite(session.expiresAt) &&
    !!session.workspaceId
  if (!valid) {
    console.warn('[persona] refusing to store a malformed persona session envelope')
    return false
  }
  const store = storage()
  if (!store) return false
  try {
    store.setItem(KEY, JSON.stringify(session))
    return true
  } catch {
    return false
  }
}

export function clearPersonaSession() {
  const KEY = 'symbols_persona_session'
  try {
    if (typeof globalThis.sessionStorage !== 'undefined') {
      globalThis.sessionStorage.removeItem(KEY)
    }
  } catch {
    /* locked storage */
  }
}

export function personaSessionExpired(session, nowMs) {
  if (!session || !Number.isFinite(session.expiresAt)) return false
  const now = Number.isFinite(nowMs) ? nowMs : Date.now()
  return session.expiresAt <= now
}

// The workspace-project token provider hook (sdkClient.js reads this on
// EVERY /workspace-project/* request): the persona token while a session
// blob exists AND its workspace binding matches the tab's active-workspace
// pointer; null otherwise (provider falls through to the base token).
//
// The workspace guard is the client half of the worker's cross-tenant
// refusal: on a DEFINITE pointer mismatch (both sides known) the blob is
// cleared — the session is workspace-bound server-side and can never be
// valid again in this tab, and clearing keeps the NEW workspace usable
// instead of failing every request with a cross-tenant sid. An unknown
// pointer (null — bare load, locked storage) keeps the persona: the server
// cross-check is the authority, and dropping the lens on a maybe is the
// silent-privilege-restore direction this module refuses to take.
export function readPersonaWorkspaceToken() {
  const KEY = 'symbols_persona_session'
  const storage = () => {
    try {
      return typeof globalThis.sessionStorage !== 'undefined' ? globalThis.sessionStorage : null
    } catch {
      return null
    }
  }
  const store = storage()
  if (!store) return null
  let parsed = null
  try {
    const raw = store.getItem(KEY)
    parsed = raw ? JSON.parse(raw) : null
  } catch {
    parsed = null
  }
  if (!parsed || typeof parsed.token !== 'string' || !parsed.token) return null
  let pointer = null
  try {
    pointer = getActiveWorkspacePointer() || null
  } catch {
    pointer = null
  }
  if (
    pointer &&
    typeof parsed.workspaceId === 'string' &&
    parsed.workspaceId &&
    String(pointer) !== String(parsed.workspaceId)
  ) {
    try {
      store.removeItem(KEY)
    } catch {
      /* locked storage */
    }
    console.warn(
      '[persona] persona session was bound to a different workspace — cleared (base session restored)'
    )
    return null
  }
  return parsed.token
}

// The DOMQL-state reflection of a stored session — everything EXCEPT the
// token (the JWT never enters state; `root._personaSession` is inspectable/
// serializable UI truth, not a credential). Null in → null out.
export function personaStateFromSession(session) {
  if (!session || typeof session !== 'object') return null
  return {
    role: session.role,
    sid: session.sid,
    startedAt: session.startedAt,
    expiresAt: session.expiresAt,
    workspaceId: session.workspaceId,
    actorWorkspaceRole: session.actorWorkspaceRole || null
  }
}
